import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  TemplateRef,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { SplitterModule } from 'primeng/splitter';
import { HeaderActionsService } from '@tourfold/libraries/shared-ui/header-actions/header-actions.service';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { AiAssistantComponent } from '../ai-assistant.component';
import { AiAssistantPanelService } from './ai-assistant-panel.service';

/**
 * Shared "content + Ask Rob" split layout (TF-2134). Wrap a shell's main content in this
 * component to get:
 *
 * - the resizable splitter with the Rob panel beside the content (state shared across shells
 *   via {@link AiAssistantPanelService} and a common splitter state key),
 * - the header Ask Rob button enablement while a hosting shell is mounted,
 * - modal coexistence: the component mirrors the Rob panel's rendered width into the
 *   `--tf-rob-panel-width` custom property on the document root, and its stylesheet constrains
 *   PrimeNG modal masks (`.p-dialog-mask`, `.p-drawer-mask`) to the remaining content area so
 *   dialogs and their backdrops never cover the panel.
 *
 * The host's content is wrapped in a `[data-app-main]` container — the anchor for anything
 * that needs to target "the main content area" of whichever shell is active.
 *
 * Content is passed as a `TemplateRef` (`[content]`) rather than projected with `<ng-content>`
 * on purpose. PrimeNG renders splitter panels from `<ng-template>`s, and content projected into
 * one of those is created *detached* and attached a tick later — so a routed page's `ngOnInit`
 * would run before its own DOM is in the document. Components that look their DOM up eagerly
 * (`document.getElementById` in `ngOnInit`, as the BW ECharts dashboards do) then silently find
 * nothing and never render. Instantiating the host's template here with `ngTemplateOutlet`
 * creates and attaches the view in one operation, preserving the pre-TF-2134 timing.
 */
@Component({
  selector: 'tf-ai-assistant-shell',
  templateUrl: './ai-assistant-shell.component.html',
  styleUrl: './ai-assistant-shell.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SplitterModule, AiAssistantComponent, NgTemplateOutlet],
})
export class AiAssistantShellComponent implements OnDestroy {
  /** The hosting shell's main content. See the class docs for why this is a template, not slot. */
  readonly content = input.required<TemplateRef<unknown>>();

  protected readonly panelService = inject(AiAssistantPanelService);
  private readonly headerActionsService = inject(HeaderActionsService);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly document = inject(DOCUMENT);

  protected readonly splitterGutterSize = 1;
  protected readonly splitterPanelSizes = [60, 40];
  protected readonly splitterMinSizes = [35, 0];
  protected readonly splitterStateKey = 'tourfold.aiAssistant.splitterPanelSizes';

  // read: ElementRef — the ref sits on a component element, which would otherwise resolve to
  // the component instance instead of its DOM element.
  private readonly assistantElement = viewChild('assistantPanel', { read: ElementRef });
  private resizeObserver: ResizeObserver | undefined;

  constructor() {
    effect(() => {
      this.headerActionsService.setAiAssistantEnabled(
        this.resourceAccessService.canPerform('aiAssistant', 'read')
      );
    });

    // Mirror the panel's rendered width (open/close, splitter drag, window resize) into the
    // root custom property that the modal-mask rules consume.
    effect(() => {
      const element = this.assistantElement()?.nativeElement;
      this.resizeObserver?.disconnect();
      this.resizeObserver = undefined;
      if (!element || typeof ResizeObserver === 'undefined') {
        this.setRobPanelWidth(0);
        return;
      }
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries.at(-1)?.contentRect.width ?? 0;
        this.setRobPanelWidth(width);
      });
      this.resizeObserver.observe(element);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.setRobPanelWidth(0);
    this.headerActionsService.setAiAssistantEnabled(false);
  }

  private setRobPanelWidth(width: number): void {
    this.document.documentElement.style.setProperty('--tf-rob-panel-width', `${width}px`);
  }
}
